import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import { useAuthState } from "../app/AuthStateContext";
import { getOrCreateVisitorId } from "../../universal/track";
import {
  appendAttributionQueryParams,
  getStoredAttribution,
} from "../../universal/campaignAttribution";
import { clearExplicitLogout } from "../../universal/explicitLogoutGuard";
import { useTemplateTrack } from "../templates/TemplateTrackingProvider";

/** Shield mark from the PlaySafe wordmark (paths only — no logotype). */
const SHIELD_VIEWBOX = "105 0 38 34";
const SHIELD_PATHS = [
  "M 137.363 19.306 C 133.893 26.713 127.813 31.153 125.118 32.842 C 125.153 32.813 125.192 32.785 125.23 32.753 C 126.398 31.82 129.51 29.342 131.943 25.937 C 133.573 23.659 134.645 21.449 135.26 19.999 C 135.635 19.111 135.903 18.393 136.163 17.585 C 137.286 14.074 137.568 10.133 137.002 5.869 L 137.002 5.847 L 136.976 5.837 C 132.206 3.815 127.698 3.186 123.94 3.186 C 121.92 3.186 120.114 3.368 118.6 3.614 C 115.632 4.093 113.34 4.876 112.05 5.39 C 113.452 4.719 116.08 3.614 119.519 2.832 C 124.324 1.743 131.738 1.078 139.793 4.106 C 139.844 4.128 139.873 4.148 139.876 4.17 C 140.341 9.66 139.495 14.752 137.363 19.306 Z",
  "M 135.92 15.363 C 135.257 18.4 134.057 21.323 132.357 24.041 C 130.234 27.433 127.321 30.521 123.697 33.22 C 119.372 30.943 115.802 28.005 113.088 24.478 C 110.914 21.655 109.284 18.451 108.238 14.957 C 106.778 10.08 106.883 5.979 106.973 4.631 C 107.104 5.934 107.616 9.767 109.396 14.188 C 110.68 17.378 112.364 20.246 114.403 22.712 C 116.952 25.791 120.06 28.247 123.646 30.007 L 123.665 30.017 L 123.681 30.007 C 126.947 28.381 129.639 26.127 131.691 23.303 C 133.33 21.045 134.563 18.423 135.357 15.507 C 136.368 11.783 136.397 8.518 136.343 7.103 C 136.404 7.538 136.468 8.148 136.5 8.902 C 136.564 10.355 136.512 12.658 135.92 15.363 Z",
  "M 138.836 3.328 C 137.504 2.852 134.575 1.948 130.692 1.581 C 127.548 1.287 124.401 1.399 121.34 1.913 C 117.518 2.558 113.817 3.842 110.347 5.727 L 110.327 5.739 L 110.327 5.762 C 110.186 11.361 111.502 15.791 112.629 18.522 C 113.298 20.138 113.983 21.364 114.467 22.15 C 113.775 21.208 112.68 19.608 111.573 17.541 C 109.892 14.411 107.837 9.537 107.568 4.085 L 107.568 4.066 C 111.326 2.031 115.347 0.747 119.515 0.243 C 122.858 -0.16 126.306 -0.061 129.76 0.533 C 134.136 1.29 137.427 2.667 138.836 3.328 Z",
];

/** "ID" letterforms from the PlaySafe wordmark (not overlaid text). */
const SHIELD_ID_PATHS = [
  "M 119.327 9.486 L 119.327 20.738 L 116.964 20.738 L 116.964 9.486 Z",
  "M 131.572 15.028 C 131.572 16.232 131.338 17.26 130.871 18.11 C 130.403 18.963 129.757 19.614 128.934 20.061 C 128.108 20.512 127.157 20.735 126.082 20.735 L 121.52 20.735 L 121.52 9.483 L 125.601 9.483 C 127.5 9.483 128.972 9.962 130.013 10.917 C 131.053 11.872 131.575 13.242 131.575 15.024 Z M 129.197 15.028 C 129.197 13.82 128.883 12.897 128.252 12.262 C 127.621 11.626 126.722 11.307 125.553 11.307 L 123.879 11.307 L 123.879 18.918 L 125.88 18.918 C 126.559 18.918 127.144 18.758 127.637 18.442 C 128.13 18.126 128.515 17.675 128.787 17.097 C 129.059 16.519 129.197 15.826 129.197 15.028 Z",
];

function PlaySafeShield({ height = 22 }) {
  const width = (height * 38) / 34;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={SHIELD_VIEWBOX}
      width={width}
      height={height}
      aria-hidden="true"
      focusable="false"
      style={{ display: "block", flexShrink: 0 }}
    >
      {SHIELD_PATHS.map((d) => (
        <path key={d.slice(0, 24)} d={d} fill="currentColor" />
      ))}
      {SHIELD_ID_PATHS.map((d, index) => (
        <path
          key={`id-${d.slice(0, 24)}`}
          d={d}
          fill="currentColor"
          fillRule={index === 1 ? "evenodd" : "nonzero"}
        />
      ))}
    </svg>
  );
}

PlaySafeShield.propTypes = {
  height: PropTypes.number,
};

const BUTTON_LABEL_SX = {
  fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.9375rem" },
  fontWeight: 600,
  letterSpacing: "0.02em",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
};

/** Floating dots — positions/timing from Uiverse particle button; shown on hover only. */
const HOVER_PARTICLES = [
  { left: "10%", opacity: 0.45, duration: "4.5s", delay: "0.3s" },
  { left: "30%", opacity: 0.32, duration: "4.8s", delay: "0.7s" },
  { left: "25%", opacity: 0.38, duration: "4.2s", delay: "0.15s" },
  { left: "44%", opacity: 0.28, duration: "3.9s", delay: "0s" },
  { left: "50%", opacity: 0.42, duration: "3.6s", delay: "0s" },
  { left: "75%", opacity: 0.25, duration: "3.2s", delay: "2s" },
  { left: "88%", opacity: 0.4, duration: "4.2s", delay: "0.3s" },
  { left: "58%", opacity: 0.36, duration: "4.3s", delay: "0.3s" },
  { left: "98%", opacity: 0.3, duration: "4.9s", delay: "0.15s" },
  { left: "65%", opacity: 0.44, duration: "4.8s", delay: "0.3s" },
];

const PARTICLES_LAYER_SX = {
  position: "absolute",
  inset: 0,
  overflow: "hidden",
  pointerEvents: "none",
  zIndex: 0,
  opacity: 0,
  transition: "opacity 0.25s ease",
};

const BUTTON_INNER_SX = {
  position: "relative",
  zIndex: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: { xs: 0.75, md: 1 },
};

const BUTTON_BASE_SX = {
  position: "relative",
  overflow: "hidden",
  "@keyframes playsafeFloatingPoints": {
    "0%": { transform: "translateY(0)" },
    "88%": { opacity: 0.25 },
    "100%": { transform: "translateY(-48px)", opacity: 0 },
  },
  "& .playsafe-particles": PARTICLES_LAYER_SX,
  "&:hover .playsafe-particles": { opacity: 0.8 },
  "& .playsafe-particle": {
    bottom: -10,
    position: "absolute",
    width: 2,
    height: 2,
    borderRadius: "50%",
    backgroundColor: "rgba(0, 204, 221, 0.55)",
    animationName: "playsafeFloatingPoints",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
    animationPlayState: "paused",
  },
  "&:hover .playsafe-particle": {
    animationPlayState: "running",
  },
};

/**
 * PlaySafe ID OAuth sign-in — top-level redirect to edge `/auth/login`.
 */
export default function PlaySafeSignInButton({
  sx = {},
  label = "Sign in with PlaySafe ID",
  logoHeight = 22,
  showLabel = true,
  fullWidth = false,
  surface = "hero",
}) {
  const { playsafeSignInUrl, isAuthenticated } = useAuthState();
  const track = useTemplateTrack();
  const baseUrl = playsafeSignInUrl || "/auth/login";

  if (isAuthenticated) {
    return null;
  }

  function handleClick(e) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    clearExplicitLogout();
    track("sso_playsafe_clicked", { provider: "playsafe" });
    const vid = getOrCreateVisitorId();
    const separator = baseUrl.includes("?") ? "&" : "?";
    let targetUrl = `${baseUrl}${separator}surface=${encodeURIComponent(surface)}&vid=${encodeURIComponent(vid)}`;

    const acq = getStoredAttribution();
    if (acq) {
      targetUrl = appendAttributionQueryParams(targetUrl, acq);
    }

    window.location.href = targetUrl;
  }

  return (
    <Box
      sx={{
        display: "flex",
        width: fullWidth ? "100%" : "auto",
        maxWidth: fullWidth ? "100%" : "none",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <Button
        href={baseUrl}
        onClick={handleClick}
        variant="outlined"
        disableElevation
        aria-label={label}
        sx={{
          minWidth: 0,
          px: { xs: "0.85rem", md: "1.15rem" },
          ...BUTTON_BASE_SX,
          ...sx,
          width: fullWidth ? "100%" : "auto",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box className="playsafe-particles" aria-hidden>
          {HOVER_PARTICLES.map((particle, index) => (
            <Box
              key={index}
              className="playsafe-particle"
              sx={{
                left: particle.left,
                opacity: particle.opacity,
                animationDuration: particle.duration,
                animationDelay: particle.delay,
              }}
            />
          ))}
        </Box>
        <Box className="playsafe-button-inner" sx={BUTTON_INNER_SX}>
          <PlaySafeShield height={logoHeight} />
          {showLabel ? (
            <Box component="span" sx={BUTTON_LABEL_SX}>
              {label}
            </Box>
          ) : null}
        </Box>
      </Button>
    </Box>
  );
}

PlaySafeSignInButton.propTypes = {
  sx: PropTypes.object,
  label: PropTypes.string,
  logoHeight: PropTypes.number,
  showLabel: PropTypes.bool,
  fullWidth: PropTypes.bool,
  surface: PropTypes.string,
};
